import { type Dispatch, type SetStateAction } from "react"
import { View, ScrollView } from "react-native"
import { type FeaturePillProps } from "@/constants/BuyBox"
import { H5, Small } from "@/components/standard/BFText"
import { SecondaryButton } from "@/components/buttons/CommonButtons"
import { Vertical } from "@/components/standard/BFLayout"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { cn } from "@/components/util/cn"

interface FeaturePillsProps {
  title: string
  values: string[]
  options: FeaturePillProps[]
  onValuesChanged:
    | ((val: string[]) => void)
    | Dispatch<SetStateAction<string[]>>
}

const FeaturePills: React.FC<FeaturePillsProps> = ({
  title,
  values,
  options,
  onValuesChanged,
}: FeaturePillsProps) => {
  const { screenWidth } = useDeviceAttributes()

  const toggleSelection = (option: FeaturePillProps) => {
    let newValues = [...values]
    const val = option.value
    if (val === "Any") {
      newValues = [val]
    } else {
      const anyIdx = newValues.indexOf("Any")
      if (anyIdx >= 0) {
        newValues.splice(anyIdx, 1)
      }

      const idx = newValues.indexOf(val)
      if (idx >= 0) {
        newValues.splice(idx, 1)
      } else {
        newValues.push(val)
      }
    }
    onValuesChanged(newValues)
  }

  const toggledOptions = options.reduce(
    (options: FeaturePillProps[], current: FeaturePillProps) => {
      options.push({ ...current, selected: values.includes(current.value) })
      return options
    },
    []
  )

  return (
    <Vertical className="flex flex-col justify-center">
      <View className="mt-4">
        <H5 className="text-midnight-blue mr-2">{title}</H5>
        <Small className="text-midnight-blue mb-3">
          Select more than one if needed
        </Small>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ width: screenWidth + 80, gap: 7 }}
        className="my-2 overflow-visible"
      >
        {/* Ensures Expansion */}
        {toggledOptions.map((option) => (
          <SecondaryButton
            key={`feature-buttons-${title}-${option.value}`}
            testID={`feature-buttons-${option.value}`}
            title={option.name}
            selected={option.selected}
            size="sm"
            className={cn(
              option.name === "Any" ? "max-w-[60px]" : "max-w-[50px]", // Width logic
              option.name === "Any" && option.selected ? "border-0" : "border" // Border logic
            )}
            onPress={() => {
              toggleSelection(option)
            }}
            noSelectedIcon
          />
        ))}
      </ScrollView>
    </Vertical>
  )
}

export default FeaturePills
